import { FC, Fragment, useRef, useMemo, useEffect } from "react";

import Stack from "@mui/material/Stack";
import { Column, Locales, QuestionType, Nullable } from "@simplyk/common";
import { FieldErrors, useFormContext } from "react-hook-form";
import { v4 } from "uuid";

import { DonationEmailTranslationText } from "../helpers/emailTranslationText";
import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";
import { CommunicationPreferencesTitleTranslation } from "../sharedComponents/CommunicationPreferencesTitleTranslation";
import { TranslatedFieldWysiwyg } from "../sharedComponents/TranslatedFieldWysiwyg";
import { TranslationFieldWysiwyg } from "../sharedComponents/TranslationFieldWysiwyg";
import { TranslationSection } from "../sharedComponents/TranslationSection";
import { TranslationLocaleProvider } from "../Context/TranslationLocaleContext";
import { DonationTranslationsData } from "../translation.types";

import { useTranslateAmounts } from "./useTranslateAmounts";

import { isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import {
  DonationFormChoiceOutput,
  DonationFormQuestionOutput,
  DonationFormWithUserOrOrgAccessOutput,
} from "@/common/types/trpc";
import { FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";
import { TranslationLine } from "@/components/TranslationLine/TranslationLine";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { QuestionTranslationCard } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/sharedComponents/QuestionTranslationCard";
import { TranslatedTextField } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/sharedComponents/TranslatedTextField";
import { useTranslateQuestions } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/Translations/TicketingTranslations/useTranslateQuestions";

export interface DonationTranslationColumnProps {
  data: DonationFormWithUserOrOrgAccessOutput;
  formLocale: Locales;
  translationLocale: Locales;
  defaultChequeDescription: {
    fields: string;
    translations: string;
  };
  emailText: { fields: DonationEmailTranslationText; translations: DonationEmailTranslationText };
  communicationPreferencesTitle: Nullable<string>;
  onTranslationStateChange?: (isTranslating: boolean) => void;
}

const nullFields = {
  id: undefined,
  title: undefined,
  description: undefined,
  message: undefined,
  fundraiserEmailBody: undefined,
  chequeDescription: undefined,
  communicationPreferencesTitle: undefined,
  donationFormAmounts: [],
};

const getQuestionChoices = (choices: DonationFormChoiceOutput[], locale: Locales) => {
  return choices.map((choice) => ({
    id: choice.id,
    value: choice.choiceFields?.find((field) => field.locale === locale)?.body ?? "",
    sortIndex: choice.sortIndex,
  }));
};

export const DonationTranslationColumn: FC<DonationTranslationColumnProps> = ({
  data,
  formLocale,
  translationLocale,
  defaultChequeDescription,
  emailText,
  communicationPreferencesTitle,
  onTranslationStateChange,
}) => {
  const { control, formState, register, setValue } = useFormContext<DonationTranslationsData>();

  const { t } = useTranslate();
  const defaultFields = data?.donationFormFields?.find((field) => field.locale === formLocale) || nullFields;
  const defaultTranslationFields =
    data?.donationFormFields?.find((field) => field.locale === translationLocale) || nullFields;

  const { title, description, fundraiserEmailBody } = defaultFields;
  const { fields: emailFields, translations: emailTranslations } = emailText;
  const { emailContent, emailObject, autoRenewalEmailObject, autoRenewalEmailContent } = emailFields;

  const fieldErrors = formState.errors[Column.Fields];
  const translationErrors = formState.errors[Column.Translations];

  const isRequiredRule = [isRequired(t("dashboard", "common.isRequired") as string)];
  const fieldSubjectRef = useRef<Nullable<HTMLInputElement>>(null);
  const translationSubjectRef = useRef<Nullable<HTMLInputElement>>(null);
  const autoRenewalFieldSubjectRef = useRef<Nullable<HTMLInputElement>>(null);
  const autoRenewalTranslationSubjectRef = useRef<Nullable<HTMLInputElement>>(null);

  const { isTranslating: isTranslatingAmounts, translatedAmountIds } = useTranslateAmounts({
    amounts: defaultFields?.donationFormAmounts || [],
    translationLocale,
    setValue,
    existingTranslations: defaultTranslationFields.donationFormAmounts ?? undefined,
  });

  const questionsToTranslate = useMemo(
    () =>
      data?.questions
        ?.filter((question) => question.type !== QuestionType.Name)
        .sort((first, second) => first.sortIndex - second.sortIndex) ?? [],
    [data?.questions]
  );

  const { isTranslating: isTranslatingQuestions, translatedQuestionFieldIds } = useTranslateQuestions({
    questions: questionsToTranslate,
    formLocale,
    translationLocale,
    setValue,
  });

  // Notify parent component when translation state changes
  useEffect(() => {
    const isTranslating = isTranslatingAmounts || isTranslatingQuestions;
    onTranslationStateChange?.(isTranslating);
  }, [isTranslatingAmounts, isTranslatingQuestions, onTranslationStateChange]);

  return (
    <TranslationLocaleProvider formLocale={formLocale} translationLocale={translationLocale}>
      <Stack gap={3}>
        <input style={{ display: "none" }} value={defaultFields.id} {...register(`${Column.Fields}.id`)} />
        <input
          style={{ display: "none" }}
          value={defaultTranslationFields.id}
          {...register(`${Column.Translations}.id`)}
        />
        {Boolean(title || description || description === "") && (
          <TranslationSection title={t("dashboard", "translation.campaignDetails")}>
            {title && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.campaignTitle", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.title`}
                  defaultValue={defaultFields.title}
                  error={Boolean(fieldErrors && fieldErrors["title"])}
                  errorMessage={fieldErrors && fieldErrors["title"]?.message}
                  data-test="donation-form-title-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.title`}
                  defaultFieldValue={defaultFields.title}
                  defaultTranslationFieldValue={defaultTranslationFields.title}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.campaignTitle", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  error={Boolean(translationErrors && translationErrors["title"])}
                  errorMessage={translationErrors && translationErrors["title"]?.message}
                  dataTest="donation-form-title-translation"
                />
              </TranslationLine>
            )}
            {(description || description === "") && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  id={`${Column.Fields}.description`}
                  name={`${Column.Fields}.description`}
                  defaultValue={defaultFields.description}
                  keyName={`${Column.Fields}.description`}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["description"]}
                  config={FroalaConfig.Minimalist}
                  data-test="donation-form-description-field"
                />
                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.description`}
                  defaultFieldValue={defaultFields.description}
                  defaultTranslationFieldValue={defaultTranslationFields.description}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={fieldSubjectRef}
                  error={translationErrors && translationErrors["description"]}
                  config={FroalaConfig.Minimalist}
                  dataTest="donation-form-description-translation"
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        {Boolean(emailObject || emailContent) && (
          <TranslationSection title={t("dashboard", "translation.confirmationEmail")}>
            {emailObject && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.emailObject`}
                  defaultValue={emailFields.emailObject}
                  error={Boolean(fieldErrors && fieldErrors["emailObject"])}
                  errorMessage={fieldErrors && fieldErrors["emailObject"]?.message}
                  inputProps={{
                    ref: fieldSubjectRef,
                  }}
                  data-test="donation-form-emailObject-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.emailObject`}
                  defaultFieldValue={emailFields.emailObject}
                  defaultTranslationFieldValue={emailTranslations.emailObject}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.subjectLine", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  inputProps={{
                    ref: translationSubjectRef,
                  }}
                  data-test="donation-form-emailObject-translation"
                  error={Boolean(translationErrors && translationErrors["emailObject"])}
                  errorMessage={translationErrors && translationErrors["emailObject"]?.message}
                  dataTest="donation-form-emailObject-translation"
                />
              </TranslationLine>
            )}
            {emailContent && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  name={`${Column.Fields}.emailContent`}
                  subjectName={`${Column.Fields}.emailObject`}
                  subjectRef={fieldSubjectRef}
                  defaultValue={emailFields.emailContent || ""}
                  attachmentKeyName={`${Column.Fields}.attachment`}
                  rules={isRequiredRule}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["emailContent"]}
                  data-test="donation-form-emailContent-field"
                  config={FroalaConfig.Email}
                />
                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.emailContent`}
                  defaultFieldValue={emailFields.emailContent}
                  defaultTranslationFieldValue={emailTranslations.emailContent}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={translationSubjectRef}
                  attachmentKeyName={`${Column.Translations}.attachment`}
                  rules={isRequiredRule}
                  error={translationErrors && translationErrors["emailContent"]}
                  dataTest="donation-form-emailContent-translation"
                  config={FroalaConfig.Email}
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        {Boolean(autoRenewalEmailObject || autoRenewalEmailContent) && (
          <TranslationSection title={t("dashboard", "translation.autoRenewalEmail")}>
            {autoRenewalEmailObject && (
              <TranslationLine>
                <DashboardTextField
                  label={t("dashboard", "translation.subjectLine", { lng: getTranslationLabelLanguage(formLocale) })}
                  rules={isRequiredRule}
                  control={control}
                  name={`${Column.Fields}.autoRenewalEmailObject`}
                  defaultValue={emailFields.autoRenewalEmailObject}
                  error={Boolean(fieldErrors && fieldErrors["autoRenewalEmailObject"])}
                  errorMessage={fieldErrors && fieldErrors["autoRenewalEmailObject"]?.message}
                  inputProps={{ ref: autoRenewalFieldSubjectRef }}
                  data-test="donation-form-autoRenewalEmailObject-field"
                />
                <TranslatedTextField
                  fieldName={`${Column.Translations}.autoRenewalEmailObject`}
                  defaultFieldValue={emailFields.autoRenewalEmailObject}
                  defaultTranslationFieldValue={emailTranslations.autoRenewalEmailObject}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  label={t("dashboard", "translation.subjectLine", {
                    lng: getTranslationLabelLanguage(translationLocale),
                  })}
                  rules={isRequiredRule}
                  control={control}
                  inputProps={{ ref: autoRenewalTranslationSubjectRef }}
                  data-test="donation-form-autoRenewalEmailObject-translation"
                  error={Boolean(translationErrors && translationErrors["autoRenewalEmailObject"])}
                  errorMessage={translationErrors && translationErrors["autoRenewalEmailObject"]?.message}
                  dataTest="donation-form-autoRenewalEmailObject-translation"
                />
              </TranslationLine>
            )}
            {autoRenewalEmailContent && (
              <TranslationLine>
                <TranslationFieldWysiwyg
                  name={`${Column.Fields}.autoRenewalEmailContent`}
                  subjectName={`${Column.Fields}.autoRenewalEmailObject`}
                  subjectRef={autoRenewalFieldSubjectRef}
                  defaultValue={emailFields.autoRenewalEmailContent || undefined}
                  rules={isRequiredRule}
                  heightMinOver310={400}
                  error={fieldErrors && fieldErrors["autoRenewalEmailContent"]}
                  data-test="donation-form-autoRenewalEmailContent-field"
                  config={FroalaConfig.Email}
                />
                <TranslatedFieldWysiwyg
                  fieldName={`${Column.Translations}.autoRenewalEmailContent`}
                  defaultFieldValue={emailFields.autoRenewalEmailContent}
                  defaultTranslationFieldValue={emailTranslations.autoRenewalEmailContent}
                  setValue={setValue}
                  translationLocale={translationLocale}
                  subjectRef={autoRenewalTranslationSubjectRef}
                  rules={isRequiredRule}
                  error={translationErrors && translationErrors["autoRenewalEmailContent"]}
                  dataTest="donation-form-autoRenewalEmailContent-translation"
                  config={FroalaConfig.Email}
                />
              </TranslationLine>
            )}
          </TranslationSection>
        )}
        {fundraiserEmailBody && (
          <TranslationSection title={t("dashboard", "translation.fundraiserEmail")}>
            <TranslationLine>
              <TranslationFieldWysiwyg
                id={`${Column.Fields}.fundraiserEmailBody`}
                name={`${Column.Fields}.fundraiserEmailBody`}
                defaultValue={defaultFields.fundraiserEmailBody || ""}
                keyName={`${Column.Fields}.fundraiserEmailBody`}
                heightMinOver310={400}
                error={fieldErrors && fieldErrors["fundraiserEmailBody"]}
                config={FroalaConfig.Minimalist}
                data-test="donation-form-fundraiserEmailBody-field"
              />
              <TranslatedFieldWysiwyg
                fieldName={`${Column.Translations}.fundraiserEmailBody`}
                defaultFieldValue={defaultFields.fundraiserEmailBody}
                defaultTranslationFieldValue={defaultTranslationFields.fundraiserEmailBody}
                setValue={setValue}
                translationLocale={translationLocale}
                subjectRef={fieldSubjectRef}
                error={translationErrors && translationErrors["fundraiserEmailBody"]}
                config={FroalaConfig.Minimalist}
                dataTest="donation-form-fundraiserEmailBody-translation"
              />
            </TranslationLine>
          </TranslationSection>
        )}
        {defaultChequeDescription.fields && (
          <TranslationSection title={t("dashboard", "translation.paymentInstructions")}>
            <TranslationLine>
              <DashboardTextField
                label={t("dashboard", "translation.chequeDescription", {
                  lng: getTranslationLabelLanguage(formLocale),
                })}
                rules={isRequiredRule}
                control={control}
                name={`${Column.Fields}.chequeDescription`}
                defaultValue={defaultChequeDescription.fields}
                error={Boolean(fieldErrors && fieldErrors["chequeDescription"])}
                errorMessage={fieldErrors && fieldErrors["chequeDescription"]?.message}
                multiline
                rows={4}
                maxRows={8}
                data-test="donation-form-cheque-field"
              />
              <TranslatedTextField
                fieldName={`${Column.Translations}.chequeDescription`}
                defaultFieldValue={defaultChequeDescription.translations}
                defaultTranslationFieldValue={defaultTranslationFields.chequeDescription}
                setValue={setValue}
                translationLocale={translationLocale}
                label={t("dashboard", "translation.chequeDescription", {
                  lng: getTranslationLabelLanguage(translationLocale),
                })}
                rules={isRequiredRule}
                control={control}
                error={Boolean(translationErrors && translationErrors["chequeDescription"])}
                errorMessage={translationErrors && translationErrors["chequeDescription"]?.message}
                multiline
                rows={4}
                maxRows={8}
                dataTest="donation-form-cheque-translation"
              />
            </TranslationLine>
          </TranslationSection>
        )}
        {Boolean(defaultFields.donationFormAmounts?.length) && (
          <TranslationSection title={t("dashboard", "translation.donationAmounts")}>
            {defaultFields.donationFormAmounts?.map((donationAmountField, index) => (
              <Fragment key={donationAmountField.id}>
                {["id", "isRecurrent", "amount", "recurrenceInterval"].map((field) => (
                  <Fragment key={field}>
                    <input
                      type="hidden"
                      {...register(`${Column.Fields}.donationFormAmounts.${index}.${field}` as never)}
                    />
                    <input
                      type="hidden"
                      {...register(`${Column.Translations}.donationFormAmounts.${index}.${field}` as never)}
                    />
                  </Fragment>
                ))}
                <TranslationLine>
                  <DashboardTextField
                    label={t("dashboard", "translation.amount", {
                      index: index + 1,
                      lng: getTranslationLabelLanguage(formLocale),
                    })}
                    control={control}
                    name={`${Column.Fields}.donationFormAmounts.${index}.message`}
                    defaultValue={donationAmountField.message}
                    error={Boolean(fieldErrors && fieldErrors["donationFormAmounts"]?.[index]?.["message"])}
                    errorMessage={
                      fieldErrors &&
                      fieldErrors["donationFormAmounts"]?.[index]?.message &&
                      t("dashboard", `${fieldErrors["donationFormAmounts"]?.[index]?.message}` as never)
                    }
                  />
                  {isTranslatingAmounts &&
                  Boolean(donationAmountField.message) &&
                  !translatedAmountIds.has(donationAmountField.id) ? (
                    <DashboardSkeleton
                      variant="rounded"
                      width="100%"
                      sx={(theme) => ({ height: theme.spacing(7.5) })}
                    />
                  ) : (
                    <TranslatedTextField
                      fieldName={`${Column.Translations}.donationFormAmounts.${index}.message`}
                      defaultFieldValue={donationAmountField.message}
                      defaultTranslationFieldValue={defaultTranslationFields.donationFormAmounts?.[index]?.message}
                      setValue={setValue}
                      translationLocale={translationLocale}
                      label={t("dashboard", "translation.amount", {
                        index: index + 1,
                        lng: getTranslationLabelLanguage(translationLocale),
                      })}
                      control={control}
                      error={Boolean(
                        translationErrors && translationErrors["donationFormAmounts"]?.[index]?.["amount"]
                      )}
                      errorMessage={
                        translationErrors && translationErrors["donationFormAmounts"]?.[index]?.["amount"]?.message
                      }
                      dataTest="donation-form-amount-translation"
                      skipTranslation={true}
                    />
                  )}
                </TranslationLine>
              </Fragment>
            ))}
          </TranslationSection>
        )}

        {questionsToTranslate.length > 0 && (
          <TranslationSection title={t("dashboard", "translation.questions")}>
            {questionsToTranslate.map((question, index) => {
              const defaultFields = question.questionFields?.find((field) => field.locale === formLocale);
              const defaultTranslationFields = question.questionFields?.find(
                (field) => field.locale === translationLocale
              );
              return (
                <TranslationLine key={question.id}>
                  <QuestionTranslationCard
                    index={index}
                    formLocale={formLocale}
                    control={control}
                    register={register}
                    namePrefix={`${Column.Fields}.questionsFields.${index}`}
                    defaultValue={{
                      id: defaultFields?.id || "",
                      body: defaultFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices((question as DonationFormQuestionOutput).choices ?? [], formLocale),
                    }}
                    bodyErrorMessage={fieldErrors?.["questionsFields"]?.[index]?.["body"]?.message}
                    choiceErrors={fieldErrors?.["questionsFields"]?.[index]?.["choices"] as FieldErrors}
                    isTranslationColumn={false}
                    translationLocale={translationLocale}
                    setValue={setValue}
                  />

                  <QuestionTranslationCard
                    index={index}
                    formLocale={formLocale}
                    control={control}
                    register={register}
                    namePrefix={`${Column.Translations}.questionsFields.${index}`}
                    defaultValue={{
                      id: defaultTranslationFields?.id || v4(),
                      body: defaultTranslationFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices(
                        (question as DonationFormQuestionOutput).choices ?? [],
                        translationLocale
                      ),
                    }}
                    bodyErrorMessage={translationErrors?.["questionsFields"]?.[index]?.["body"]?.message}
                    choiceErrors={translationErrors?.["questionsFields"]?.[index]?.["choices"] as FieldErrors}
                    isTranslationColumn={true}
                    translationLocale={translationLocale}
                    setValue={setValue}
                    textToTranslate={{
                      id: defaultFields?.id || "",
                      body: defaultFields?.body || "",
                      questionId: question.id,
                      choices: getQuestionChoices((question as DonationFormQuestionOutput).choices ?? [], formLocale),
                    }}
                    isLoading={isTranslatingQuestions && !translatedQuestionFieldIds.has(question.id)}
                  />
                </TranslationLine>
              );
            })}
          </TranslationSection>
        )}
        <CommunicationPreferencesTitleTranslation
          questionTitle={communicationPreferencesTitle}
          savedTranslation={defaultTranslationFields.communicationPreferencesTitle}
          formLocale={formLocale}
          translationLocale={translationLocale}
        />
      </Stack>
    </TranslationLocaleProvider>
  );
};
